/** venture/certplan.ts — 투자확인서 발급 방식 선택·변경 규칙 (2026-09-24)
 *  · 한 회사에 방식을 여러 개 고를 수 있다 (예: 조합 인수 전환사채 + 자본금 증자분 → 확인서 2장).
 *  · 변경은 어디서든(등록정보·14단계) 가능하고, 이력(언제·무엇→무엇·메모)을 남긴다.
 *  · 방식별 14단계 항목(s13 저장소 — 저장소 이름은 단계 재배치 전 그대로)은 키에 방식 접두가 붙어 있어 방식을 빼도 지워지지 않고 접혀 보관되며,
 *    다시 고르면 그대로 살아난다. */
import { CERT_MODES, planModes } from './types';
import type { CertPlan, CertMode, CertPlanChange } from './types';

function nowLabel(): string {
  const d = new Date();
  const p = (n: number) => String(n).padStart(2, '0');
  return d.getFullYear() + '-' + p(d.getMonth() + 1) + '-' + p(d.getDate()) + ' ' + p(d.getHours()) + ':' + p(d.getMinutes());
}
const order = (modes: CertMode[]): CertMode[] => CERT_MODES.map((x) => x[0]).filter((m) => modes.indexOf(m) >= 0);
const same = (a: CertMode[], b: CertMode[]): boolean => a.length === b.length && a.every((m, i) => m === b[i]);

/** 방식 목록 교체 — 같으면 그대로, 다르면 이력 1건 추가. mode(호환)는 첫 방식 */
export function setCertModes(plan: CertPlan, modes: CertMode[], memo: string): CertPlan {
  const cur = planModes(plan);
  const next = order(modes);
  if (same(cur, next)) return plan;
  const entry: CertPlanChange = { at: nowLabel(), from: cur.join(','), to: next.join(','), memo: (memo || '').trim() };
  return { ...plan, mode: next[0] || '', modes: next, history: [...(plan.history || []), entry] };
}

/** 방식 1개 켜고 끄기 */
export function toggleCertMode(plan: CertPlan, m: CertMode, memo: string): CertPlan {
  const cur = planModes(plan);
  return setCertModes(plan, cur.indexOf(m) >= 0 ? cur.filter((x) => x !== m) : [...cur, m], memo);
}

/** (호환) 단일 방식으로 바꾸기 */
export function changeCertMode(plan: CertPlan, to: CertMode, memo: string): CertPlan {
  return setCertModes(plan, [to], memo);
}

/** 14단계(투자확인서 발급) 저장소(s13) 키 — 방식별로 분리해 서로 다른 방식의 진행분이 섞이지 않게 한다 */
export const kitKey = (coId: string, mode: CertMode | '', slot: string): string => coId + '_' + (mode || 'none') + '_' + slot;

/** 이력에 등장했으나 지금은 빠진 방식들 (진행분 보관 표시용) */
export function previousModes(plan: CertPlan): CertMode[] {
  const cur = planModes(plan);
  const seen = new Set<CertMode>();
  (plan.history || []).forEach((h) => {
    String(h.from || '').split(',').forEach((m) => { if (m && cur.indexOf(m as CertMode) < 0) seen.add(m as CertMode); });
  });
  return order(Array.from(seen));
}

/** 이력 한 줄 표시용 — 'a,b' → 짧은 이름 */
export function modesText(csv: string, short: Record<CertMode, string>): string {
  const ms = String(csv || '').split(',').filter(Boolean) as CertMode[];
  return ms.length ? ms.map((m) => short[m] || m).join(' + ') : '없음';
}
